import React from 'react'
import { Search as SearchIcon } from '@mui/icons-material'
import s from './PackagePicker.module.css'

/*
  The campaign's "Who → Packages" choice, in the same chips + search + list
  shape as the package popup's workflow picker (PopupPackageV2.tsx) — a copy of
  that look, not shared code, so the package popup stays as it is.

  Used by the campaign dialog (keyed by title) and the admin message composer
  (keyed by id). Every package chosen collapses into one "All packages (N)" chip.
*/
export type PickerPackage<K> = {
  key: K
  title: string
  free?: boolean
  /** not on sale any more — still listed, greyed, with this hint */
  inactive?: string
}

export function PackagePicker<K extends string | number>({ rules, value, onChange, loading }: {
  rules: PickerPackage<K>[]
  value: K[]
  onChange: (keys: K[]) => void
  loading?: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')

  const all = rules.length > 0 && rules.every((r) => value.includes(r.key))
  const chosen = rules.filter((r) => value.includes(r.key))
  const q = query.trim().toLowerCase()
  const offered = rules.filter((r) => !value.includes(r.key) && (!q || r.title.toLowerCase().includes(q)))
  const offerAll = !all && !q && rules.length > 1

  return (
    <div className={s.picker}>
      {(all || chosen.length > 0) && (
        <div className={s.chips}>
          {all ? (
            <span className={s.chip}>
              <i />
              All packages ({rules.length})
              <button type="button" onClick={() => onChange([])} aria-label="Remove all packages">×</button>
            </span>
          ) : chosen.map((r) => (
            <span key={r.key} className={`${s.chip} ${r.inactive ? s.inactive : ''}`} title={r.inactive}>
              <i />
              {r.title}{r.free && <sup className={s.free}>Free</sup>}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== r.key))} aria-label={`Remove ${r.title}`}>×</button>
            </span>
          ))}
        </div>
      )}
      <div className={s.search}>
        <SearchIcon sx={{ fontSize: 17, color: 'var(--text3)' }} />
        <input
          value={query}
          placeholder={loading ? 'Loading packages…' : 'Add a package'}
          disabled={loading}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
        />
        {open && <button type="button" className={s.done} onClick={() => setOpen(false)}>Done</button>}
      </div>
      {open && !loading && (
        <div className={s.list}>
          {offerAll && (
            <button type="button" className={`${s.item} ${s.itemAll}`} onClick={() => onChange(rules.map((r) => r.key))}>
              <i />
              <span>All packages</span>
              <small>{rules.length} total</small>
            </button>
          )}
          {offered.length ? offered.map((r) => (
            <button key={r.key} type="button" className={`${s.item} ${r.inactive ? s.inactive : ''}`} onClick={() => onChange([...value, r.key])}>
              <i />
              <span>{r.title}</span>
              {r.free && <sup className={s.free}>Free</sup>}
              {r.inactive && <small>{r.inactive}</small>}
            </button>
          )) : (
            <div className={s.none}>{q ? `No packages match “${query.trim()}”` : 'All packages are added'}</div>
          )}
        </div>
      )}
    </div>
  )
}
